import { JsonData, ExportOptions } from '../types';

export class ExportService {
  static async exportData(data: JsonData, options: ExportOptions): Promise<void> {
    switch (options.format) {
      case 'json':
        await this.exportAsJson(data, options);
        break;
      case 'csv':
        await this.exportAsCsv(data, options);
        break;
      case 'xlsx':
        await this.exportAsXlsx(data, options);
        break;
      case 'xml':
        await this.exportAsXml(data, options);
        break;
      default:
        throw new Error(`Unsupported export format: ${options.format}`);
    }
  }

  private static async exportAsJson(data: JsonData, options: ExportOptions): Promise<void> {
    let exportData: any = data;

    if (options.includeMetadata) {
      exportData = {
        metadata: {
          exportedAt: new Date().toISOString(),
          format: 'json',
          version: '1.0'
        },
        data
      };
    }

    if (options.includeSchema) {
      const schema = this.generateBasicSchema(data);
      exportData = {
        ...exportData,
        schema
      };
    }

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    this.downloadBlob(blob, 'data.json');
  }

  private static async exportAsCsv(data: JsonData, options: ExportOptions): Promise<void> {
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('CSV export requires an array of objects');
    }

    // Extract headers from the first object
    const headers = new Set<string>();
    data.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        Object.keys(item).forEach(key => headers.add(key));
      }
    });

    const headerArray = Array.from(headers);
    
    // Build CSV content
    let csvContent = headerArray.join(',') + '\n';
    
    data.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        const row = headerArray.map(header => {
          const value = item[header];
          if (value === null || value === undefined) return '';
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return String(value);
        });
        csvContent += row.join(',') + '\n';
      }
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    this.downloadBlob(blob, 'data.csv');
  }

  private static async exportAsXlsx(data: JsonData, options: ExportOptions): Promise<void> {
    // For XLSX export, we'll create a simple CSV format that can be opened in Excel
    // In a real implementation, you'd use a library like xlsx
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('XLSX export requires an array of objects');
    }

    // Similar to CSV but with tab separation for better Excel compatibility
    const headers = new Set<string>();
    data.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        Object.keys(item).forEach(key => headers.add(key));
      }
    });

    const headerArray = Array.from(headers);
    let xlsxContent = headerArray.join('\t') + '\n';
    
    data.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        const row = headerArray.map(header => {
          const value = item[header];
          if (value === null || value === undefined) return '';
          return String(value);
        });
        xlsxContent += row.join('\t') + '\n';
      }
    });

    const blob = new Blob([xlsxContent], { type: 'application/vnd.ms-excel' });
    this.downloadBlob(blob, 'data.xls');
  }

  private static async exportAsXml(data: JsonData, options: ExportOptions): Promise<void> {
    const xmlContent = this.jsonToXml(data, 'root');
    
    let fullXml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    
    if (options.includeMetadata) {
      fullXml += '<metadata>\n';
      fullXml += `  <exportedAt>${new Date().toISOString()}</exportedAt>\n`;
      fullXml += `  <format>xml</format>\n`;
      fullXml += `  <version>1.0</version>\n`;
      fullXml += '</metadata>\n';
    }
    
    fullXml += xmlContent;

    const blob = new Blob([fullXml], { type: 'application/xml' });
    this.downloadBlob(blob, 'data.xml');
  }

  private static jsonToXml(obj: any, rootName: string): string {
    let xml = `<${rootName}>\n`;
    
    if (obj === null || obj === undefined) {
      xml += '</' + rootName + '>\n';
      return xml;
    }

    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        xml += this.jsonToXml(item, 'item');
      });
    } else if (typeof obj === 'object') {
      Object.entries(obj).forEach(([key, value]) => {
        // Sanitize key for XML
        const xmlKey = key.replace(/[^a-zA-Z0-9_]/g, '_');
        xml += this.jsonToXml(value, xmlKey);
      });
    } else {
      // Escape XML special characters
      const escapedValue = String(obj)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      xml += escapedValue + '\n';
    }
    
    xml += `</${rootName}>\n`;
    return xml;
  }

  private static generateBasicSchema(data: JsonData): any {
    const analyzeType = (value: any): any => {
      if (value === null) return { type: 'null' };
      if (Array.isArray(value)) {
        return {
          type: 'array',
          items: value.length > 0 ? analyzeType(value[0]) : { type: 'any' }
        };
      }
      if (typeof value === 'object') {
        const properties: any = {};
        Object.entries(value).forEach(([key, val]) => {
          properties[key] = analyzeType(val);
        });
        return { type: 'object', properties };
      }
      return { type: typeof value };
    };

    return analyzeType(data);
  }

  private static downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  static copyToClipboard(data: JsonData): Promise<void> {
    const jsonString = JSON.stringify(data, null, 2);
    return navigator.clipboard.writeText(jsonString);
  }

  static generateShareableLink(data: JsonData): string {
    // In a real implementation, this would upload the data to a service
    // and return a shareable link. For now, we'll create a data URL
    const jsonString = JSON.stringify(data);
    const encoded = btoa(jsonString);
    return `${window.location.origin}?data=${encoded}`;
  }

  static parseSharedData(): JsonData | null {
    const urlParams = new URLSearchParams(window.location.search);
    const dataParam = urlParams.get('data');
    
    if (!dataParam) return null;
    
    try {
      const decoded = atob(dataParam);
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }
}
